/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { Icon } from '@/components/Icon';
import { useUiStore, type TourKind } from '@/stores/uiStore';
import { firstVisible, isOnScreen } from './geometry';
import { INVITE_KINDS, resolveTour, tourForPath } from './tourSteps';
import './tour.css';

/** Let the page settle (and the user get their bearings) before offering anything. */
export const INVITE_DELAY = 1500;
/** Pages still loading are re-checked this often, a limited number of times. */
const RECHECK_INTERVAL = 1000;
const MAX_CHECKS = 10;

/**
 * Open menus, popovers and modal dialogs: while any is up the invitation steps aside. Some menus
 * live inside sticky headers whose stacking context is below the invitation's, so stepping aside
 * is the only reliable way never to cover them.
 */
const MENU_OPEN = [
  '[aria-haspopup]:not([aria-haspopup="false"])[aria-expanded="true"]',
  '[role="menu"]',
  '[data-radix-popper-content-wrapper]',
  '[role="dialog"][aria-modal="true"]',
].join(', ');

const overlaps = (a: DOMRect, b: DOMRect) =>
  a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;

/**
 * True when a menu or popover is open, or when anything a disclosure button has expanded (a
 * dropdown such as the admin tool switcher, a combobox list) reaches into the invitation's area.
 * Disclosures that open elsewhere on the page (a filter row, a tree branch) do not count.
 */
function popupOpen(area: DOMRect | null): boolean {
  if ([...document.querySelectorAll(MENU_OPEN)].some((el) => isOnScreen(el, false))) return true;
  if (!area) return false;
  return [...document.querySelectorAll('[aria-expanded="true"][aria-controls]')].some((trigger) => {
    const popup = document.getElementById(trigger.getAttribute('aria-controls')!);
    return !!popup && isOnScreen(popup, false) && overlaps(popup.getBoundingClientRect(), area);
  });
}

/** True while something the invitation would cover is open; watched only while `active`. */
function usePopupOpen(active: boolean): boolean {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!active) return;
    // Where the invitation sits (or last sat, while it is stepped aside).
    let area: DOMRect | null = null;
    let frame = 0;
    const check = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        area = document.querySelector('.tour-invite')?.getBoundingClientRect() ?? area;
        setOpen(popupOpen(area));
      });
    };
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-expanded'],
    });
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [active]);
  return active && open;
}

/**
 * First-visit offer of the current page's quick tour: a small card in the corner with Start and
 * Not now. It never starts a tour by itself, waits for the safety disclaimer, appears once per
 * page tour, only when that tour has something on screen to show, and steps aside whenever a
 * menu, popover, dialog or the nav drawer is open.
 */
export function TourInvite() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const kind = tourForPath(pathname);
  const seen = useUiStore((s) => s.toursSeen.includes(kind));
  const tourOpen = useUiStore((s) => s.tourOpen);
  const blocked = useUiStore((s) => s.disclaimerBlocking);
  const startTour = useUiStore((s) => s.startTour);
  const dismiss = useUiStore((s) => s.dismissTourInvite);
  // The phone nav drawer and the vehicle picker panel sit below the invitation's layer.
  const overlayOpen = useUiStore((s) => s.navOpen || s.pickerOpen);
  const titleId = useId();
  const [ready, setReady] = useState<TourKind | null>(null);

  const eligible = INVITE_KINDS.includes(kind) && !seen && !tourOpen && !blocked;

  useEffect(() => {
    if (!eligible) return;
    let checks = 0;
    let timer = 0;
    const check = () => {
      checks += 1;
      // The page tour must have its key stops on screen (not fall back to the general tour).
      if (resolveTour(kind, firstVisible).kind === kind) setReady(kind);
      else if (checks < MAX_CHECKS) timer = window.setTimeout(check, RECHECK_INTERVAL);
    };
    timer = window.setTimeout(check, INVITE_DELAY);
    return () => window.clearTimeout(timer);
  }, [eligible, kind]);

  const covering = usePopupOpen(eligible && ready === kind);
  const show = eligible && ready === kind && !covering && !overlayOpen;
  const page = t(`tour.page.${kind}`);

  return (
    // Always mounted, so the card is announced politely when it appears.
    <div className="tour-invite-live" aria-live="polite">
      {show && (
        <section
          className="tour-invite"
          aria-labelledby={titleId}
          onKeyDown={(e) => {
            if (e.key === 'Escape') dismiss(kind);
          }}
        >
          <span className="tour-invite-ic" aria-hidden="true">
            <Icon name="compass" size={20} />
          </span>
          <div className="tour-invite-text">
            <h2 id={titleId} className="tour-invite-title">
              {t('tour.invite.title', { page })}
            </h2>
            <p className="tour-invite-body">{t('tour.invite.body')}</p>
            <div className="tour-invite-actions">
              <button
                type="button"
                className="tour-btn tour-btn--primary"
                onClick={() => startTour(kind)}
              >
                {t('tour.invite.start')}
              </button>
              <button
                type="button"
                className="tour-btn tour-btn--ghost"
                onClick={() => dismiss(kind)}
              >
                {t('tour.invite.later')}
              </button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
